// Web Speech API helpers for voice input + text-to-speech (read aloud)

// ---------- Speech Recognition (voice input) ----------
export function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return SR || null;
}

export function isSpeechSupported() {
  return !!getSpeechRecognition();
}

export function createRecognizer({ onResult, onEnd, onError, lang = "en-IN" } = {}) {
  const SR = getSpeechRecognition();
  if (!SR) return null;
  const rec = new SR();
  rec.lang = lang;
  rec.interimResults = true;
  rec.continuous = false;
  rec.maxAlternatives = 1;

  let finalText = "";
  rec.onresult = (e) => {
    let interim = "";
    finalText = "";
    for (let i = 0; i < e.results.length; i++) {
      const t = e.results[i][0].transcript;
      if (e.results[i].isFinal) finalText += t;
      else interim += t;
    }
    onResult && onResult({ final: finalText, interim, combined: (finalText + " " + interim).trim() });
  };
  rec.onend = () => onEnd && onEnd(finalText.trim());
  rec.onerror = (e) => onError && onError(e.error || "speech_error");
  return rec;
}

// ---------- Text-to-Speech ----------

export function isTTSSupported() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

let cachedVoices = null;
export function getVoices() {
  if (!isTTSSupported()) return [];
  cachedVoices = window.speechSynthesis.getVoices() || [];
  return cachedVoices;
}

if (isTTSSupported()) {
  window.speechSynthesis.onvoiceschanged = () => {
    cachedVoices = window.speechSynthesis.getVoices() || [];
  };
  getVoices();
}

function pickVoice() {
  const voices = getVoices();
  if (!voices.length) return null;
  return (
    voices.find((v) => /en[-_]IN/i.test(v.lang)) ||
    voices.find((v) => /en[-_](US|GB|AU)/i.test(v.lang)) ||
    voices.find((v) => /^en/i.test(v.lang)) ||
    voices[0]
  );
}

export function stripMarkdown(s) {
  if (!s) return "";
  return s
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/[*_~]/g, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\s+/g, " ")
    .trim();
}

// Split into ~chunkSize-char chunks at sentence boundaries.
// Keep chunks small so each utterance finishes well before Chrome's ~15 sec
// TTS timeout, which otherwise silently halts long playback.
function chunkText(text, chunkSize = 140) {
  if (text.length <= chunkSize) return [text];
  const sentences = text.match(/[^.!?\n]+[.!?]+|[^.!?\n]+$/g) || [text];
  const chunks = [];
  let buf = "";
  for (const sRaw of sentences) {
    const s = sRaw.trim();
    if (!s) continue;
    if ((buf + " " + s).trim().length <= chunkSize) {
      buf = (buf + " " + s).trim();
      continue;
    }
    if (buf) {
      chunks.push(buf);
      buf = "";
    }
    if (s.length <= chunkSize) {
      buf = s;
    } else {
      const words = s.split(/\s+/);
      let part = "";
      for (const w of words) {
        if ((part + " " + w).trim().length > chunkSize) {
          if (part) chunks.push(part.trim());
          part = w;
        } else {
          part = (part + " " + w).trim();
        }
      }
      if (part) buf = part;
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

// Active "job" — only one TTS playback runs at a time across the whole app.
let _activeJob = null;

/**
 * Play `text` aloud, chunked + chained. Returns a handle (or null if unsupported).
 * Each chunk is small (~140 chars / ~7 sec of speech) and the next chunk only
 * starts after the previous one's onend fires. This avoids:
 *   • Chrome's ~15 sec silent-halt bug
 *   • Queue drops after a recent cancel()
 *   • Android Chrome's per-utterance ~200 char limit
 *
 * Pause/Resume map to native speechSynthesis pause/resume.
 * Stop fully cancels and discards remaining chunks.
 */
export function speak(text, { rate = 1, pitch = 1, onEnd, onStart, onPause, onResume } = {}) {
  if (!isTTSSupported()) return null;
  const clean = stripMarkdown(text);
  if (!clean) return null;

  // Kill anything currently playing
  const prevJob = _activeJob;
  if (prevJob) prevJob.active = false;
  window.speechSynthesis.cancel();

  const chunks = chunkText(clean, 140);
  const voice = pickVoice();

  const job = {
    active: true,
    idx: 0,
    startFired: false,
    chunks,
    callbacks: { onEnd, onStart, onPause, onResume },
  };
  _activeJob = job;

  const playChunk = () => {
    if (!job.active) return;
    if (job.idx >= job.chunks.length) {
      if (_activeJob === job) _activeJob = null;
      onEnd && onEnd();
      return;
    }
    const text = job.chunks[job.idx];
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.lang = voice?.lang || "en-IN";
    u.rate = rate;
    u.pitch = pitch;

    u.onstart = () => {
      if (!job.startFired) {
        job.startFired = true;
        onStart && onStart();
      }
    };
    u.onpause = () => onPause && onPause();
    u.onresume = () => onResume && onResume();
    u.onend = () => {
      if (!job.active) return;
      job.idx += 1;
      // Tiny gap helps Android engines reset between utterances cleanly.
      setTimeout(playChunk, 25);
    };
    u.onerror = () => {
      if (!job.active) return;
      // Skip a failed chunk and keep going.
      job.idx += 1;
      setTimeout(playChunk, 25);
    };

    window.speechSynthesis.speak(u);
  };

  // Small initial delay so a fresh cancel() above can fully release the engine
  // (especially on Android Chrome) before we queue the first utterance.
  setTimeout(playChunk, prevJob ? 200 : 30);
  return job;
}

export function pauseSpeaking() {
  if (!isTTSSupported()) return;
  window.speechSynthesis.pause();
}

export function resumeSpeaking() {
  if (!isTTSSupported()) return;
  window.speechSynthesis.resume();
}

export function stopSpeaking() {
  if (_activeJob) {
    _activeJob.active = false;
    _activeJob = null;
  }
  if (isTTSSupported()) window.speechSynthesis.cancel();
}

export function isSpeaking() {
  if (!isTTSSupported()) return false;
  return window.speechSynthesis.speaking;
}

export function isPaused() {
  if (!isTTSSupported()) return false;
  return window.speechSynthesis.paused;
}
